import { createContext, useContext, useState, ReactNode } from "react";
import { useProfileContext } from "./ProfileContext";
import { Role } from "../core/models/entities/Role";

interface PriceVisibilityContextProps {
  hidePrices: boolean;
  showCustomerPrices: boolean;
  togglePriceVisibility: () => void;
  toggleCustomerPrices: () => void;
}

const PriceVisibilityContext = createContext<
  PriceVisibilityContextProps | undefined
>(undefined);

interface PriceVisibilityProviderProps {
  children: ReactNode;
}

export function PriceVisibilityProvider({
  children,
}: PriceVisibilityProviderProps) {
  const [hidePrices, setHidePrices] = useState<boolean>(false);
  const [showCustomerPrices, setShowCustomerPrices] = useState<boolean>(false);
  const { role, organization } = useProfileContext();

  const togglePriceVisibility = () => {
    setHidePrices((prev) => !prev);
  };

  const toggleCustomerPrices = () => {
    if (role === Role.EndUser) return;
    setShowCustomerPrices((prev) => !prev);
  };

  const customerHasPricesHidden =
    role === Role.EndUser && organization?.hidePrices === true;

  const effectiveHidePrices = hidePrices || customerHasPricesHidden;

  const effectiveShowCustomerPrices =
    role === Role.EndUser ? true : showCustomerPrices;

  return (
    <PriceVisibilityContext.Provider
      value={{
        hidePrices: effectiveHidePrices,
        showCustomerPrices: effectiveShowCustomerPrices,
        togglePriceVisibility,
        toggleCustomerPrices,
      }}
    >
      {children}
    </PriceVisibilityContext.Provider>
  );
}

export function usePriceVisibility() {
  const context = useContext(PriceVisibilityContext);
  if (context === undefined) {
    throw new Error(
      "usePriceVisibility must be used within a PriceVisibilityProvider"
    );
  }
  return context;
}
